import { getProposal, getProposalItems } from 'services/actions/ai_management_proposal';
import type { ProposalItemResponse } from 'services/actions/ai_management_proposal';
import {
  getProposalCompany,
  listMonitoredCompanies,
} from 'services/actions/ai_management_proposal_company';
import { getProposalConditions } from 'services/actions/ai_management_proposal_conditions';
import type { ProposalDocumentBidding, ProposalDocumentInput } from './structure';

// Teto de itens por página do back (ProposalItems::MAX_PER_PAGE).
const PER_PAGE = 200;

const LOAD_ERROR = 'Não foi possível carregar os dados da proposta.';

// O interceptor global do Api resolve 4xx sem rejeitar: o erro vem pelo status.
const ok = <T>(res: { data: T; status?: number }): T => {
  if (!res || (res.status ?? 200) >= 400 || !res.data) throw new Error(LOAD_ERROR);
  return res.data;
};

const loadSelectedItems = async (biddingId: number) => {
  const first = ok(await getProposalItems(biddingId, { selecionados: true, per_page: PER_PAGE }));
  const itens: ProposalItemResponse[] = [...first.itens];
  // Uma página por vez, em ordem: o back pagina sobre a lista filtrada e não há ganho em paralelo.
  for (let page = 2; page <= (first.paginacao?.total_pages ?? 1); page += 1) {
    const query = { selecionados: true, per_page: PER_PAGE, page };
    // eslint-disable-next-line no-await-in-loop
    const next = ok(await getProposalItems(biddingId, query));
    itens.push(...next.itens);
  }
  const items: ProposalDocumentInput['items'] = {
    itens,
    colunas: first.colunas,
    casas_decimais: first.casas_decimais,
    julgamento: first.julgamento,
  };
  return items;
};

// Sem CNPJ confirmado na etapa "Empresa e condições", usa o primeiro monitorado (padrão da etapa).
const resolveCnpj = async (empresaCnpj: string | null | undefined) => {
  if (empresaCnpj) return empresaCnpj;
  const res = await listMonitoredCompanies();
  const primeira = (res?.data?.empresas ?? [])
    .map((e) => (e.monitored_company || e.cnpj || '').replace(/\D/g, ''))
    .find((cnpj) => cnpj !== '');
  return primeira ?? null;
};

// Dados da empresa da proposta (o CNPJ salvo, ou o primeiro monitorado), para preencher modelos.
export const loadProposalCompanyData = async (biddingId: number) => {
  const proposal = ok(await getProposal(biddingId));
  const cnpj = await resolveCnpj(proposal.empresa_cnpj);
  return cnpj ? ok(await getProposalCompany(biddingId, cnpj)) : null;
};

export const loadProposalDocumentInput = async (
  biddingId: number,
  bidding: ProposalDocumentBidding,
): Promise<ProposalDocumentInput> => {
  const proposal = ok(await getProposal(biddingId));
  const [items, conditionsRes, cnpj] = await Promise.all([
    loadSelectedItems(biddingId),
    getProposalConditions(biddingId),
    resolveCnpj(proposal.empresa_cnpj),
  ]);
  const company = cnpj ? ok(await getProposalCompany(biddingId, cnpj)) : null;

  return {
    bidding,
    proposal,
    items,
    company,
    conditions: ok(conditionsRes),
    geradoEm: new Date(),
  };
};
